/**
 * SidebarStep1 - Client Information (v2 Sidebar Layout)
 * 
 * This component wraps the existing Step1 form logic with the new sidebar layout.
 * It reuses the same form fields and validation - only the layout changes.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Building2, User, MapPin, Mail, Phone, Briefcase, ChevronDown, Plus, Check } from 'lucide-react';
import WizardV2Layout from './WizardV2Layout';
import { useAuth } from '../../../contexts/TestAuthContext';
import { apiFetch } from '../../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface SidebarStep1Props {
  goToStep?: (step: number) => void;
  onNext: (data: any) => void;
  data: any;
  isSaving?: boolean;
  lastSaved?: Date | null;
  onSave?: () => void;
  highestCompletedStep?: number;
  backendCompletionPercent?: number;
  claimId?: string;
  chromeless?: boolean;
}

export default function SidebarStep1({ onNext, data, isSaving, lastSaved, onSave, goToStep, highestCompletedStep, backendCompletionPercent, claimId, chromeless }: SidebarStep1Props) {
  const { getToken } = useAuth();
  const [formData, setFormData] = useState({
    law_firm: data.law_firm || '',
    client_name: data.client_name || '',
    claimant_name: data.claimant_name || '',
    claimant_email: data.claimant_email || '',
    claimant_phone: data.claimant_phone || '',
    business_address: data.business_address || ''
  });
  
  const [showLawFirmDropdown, setShowLawFirmDropdown] = useState(false);
  const [lawFirmSearch, setLawFirmSearch] = useState('');
  const [lawFirms, setLawFirms] = useState<string[]>([]);
  const [loadingFirms, setLoadingFirms] = useState(true);
  const [activeSection, setActiveSection] = useState('business');
  const [validationError, setValidationError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch law firms from API
  useEffect(() => {
    const fetchLawFirms = async () => {
      try {
        const token = await getToken();
        const data = await apiFetch<{ law_firms: { name: string }[] }>(
          '/api/v1/law-firms',
          { 
            headers: { Authorization: `Bearer ${token}` },
            showErrorToast: false 
          }
        );
        const firmNames = (data.law_firms || []).map((f) => f.name);
        setLawFirms(firmNames);
      } catch (error) {
        console.error('Error fetching law firms:', error);
      } finally {
        setLoadingFirms(false);
      }
    };
    fetchLawFirms();
  }, [getToken]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLawFirmDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync formData with parent's data prop.
  // Depend on the specific primitive fields we read, NOT the whole `data`
  // object — the parent recreates `data` on every render, which previously
  // re-fired this effect and triggered setFormData → cascade.
  useEffect(() => {
    if (data.client_name || data.claimant_name || data.business_address) {
      setFormData(prev => ({
        ...prev,
        law_firm: data.law_firm || prev.law_firm,
        client_name: data.client_name || prev.client_name,
        claimant_name: data.claimant_name || prev.claimant_name,
        claimant_email: data.claimant_email || prev.claimant_email,
        claimant_phone: data.claimant_phone || prev.claimant_phone,
        business_address: data.business_address || prev.business_address
      }));
    }
  }, [
    data.law_firm,
    data.client_name,
    data.claimant_name,
    data.claimant_email,
    data.claimant_phone,
    data.business_address,
  ]);

  const handleSubmit = () => {
    if (!formData.client_name || !formData.claimant_name || !formData.business_address) {
      setValidationError('Please fill in all required fields');
      return;
    }
    onNext(formData);
  };

  const filteredLawFirms = lawFirms.filter(firm => 
    firm.toLowerCase().includes(lawFirmSearch.toLowerCase())
  );

  const handleAddNewLawFirm = () => {
    if (lawFirmSearch && !lawFirms.includes(lawFirmSearch)) {
      setLawFirms([...lawFirms, lawFirmSearch]);
    }
    setFormData({ ...formData, law_firm: lawFirmSearch });
    setShowLawFirmDropdown(false);
  };

  // Form validity is still local state. Completion percent is intentionally
  // NOT computed here — backend is the SSoT. See
  // docs/plans/2026-04-25-frontend-calc-drift-audit.md (Fix B).
  const isFormValid = formData.client_name && formData.claimant_name && formData.business_address;

  // Build QA items
  const qaItems = [];
  if (!formData.client_name) qaItems.push({ id: 'name', label: 'Business Name', status: 'missing' as const });
  if (!formData.business_address) qaItems.push({ id: 'address', label: 'Business Address', status: 'missing' as const });
  if (!formData.claimant_name) qaItems.push({ id: 'contact', label: 'Contact Name', status: 'missing' as const });

  // Build sections
  const sections = [
    { 
      id: 'lawfirm', 
      label: 'Law Firm', 
      isActive: activeSection === 'lawfirm',
      isComplete: !!formData.law_firm,
      onClick: () => setActiveSection('lawfirm')
    },
    { 
      id: 'business', 
      label: 'Business Info', 
      isActive: activeSection === 'business',
      isComplete: !!formData.client_name && !!formData.business_address,
      onClick: () => setActiveSection('business')
    },
    { 
      id: 'contact', 
      label: 'Contact', 
      isActive: activeSection === 'contact',
      isComplete: !!formData.claimant_name,
      onClick: () => setActiveSection('contact')
    },
  ];

  return (
    <WizardV2Layout
      currentStep={1}
      highestCompletedStep={highestCompletedStep}
      completionPercent={backendCompletionPercent}
      stepTitle="Client Information"
      stepSubtitle="Enter the details about your client's case. Fields marked with * are required."
      calculationMode={data.calculation_mode || 'standard_bi'}
      sections={sections}
      qaItems={qaItems}
      nextAction={qaItems.length > 0 ? {
        label: `Complete ${qaItems[0].label}`,
        description: 'Required to continue'
      } : undefined}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      showBack={false}
      onNext={handleSubmit}
      claimId={claimId}
      chromeless={chromeless}
      nextLabel="Continue to Event Details"
      isNextDisabled={!isFormValid}
    >
      <div className="p-6 lg:p-8">

        {validationError && (
          <p className="text-[var(--color-brand)] text-sm mb-4">{validationError}</p>
        )}

        {/* Two-column layout for full width utilization */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          
          {/* LEFT COLUMN */}
          <div className="space-y-6">
            {/* ═══════════════════════════════════════════════════════════════════
                SECTION 1: Referring Law Firm
            ═══════════════════════════════════════════════════════════════════ */}
            <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 border border-[var(--color-border)]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[var(--color-needs-human-tint)] flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-[var(--color-brand)]" />
                </div>
                <div>
                  <h3 className="cs-h3">Referring Law Firm</h3>
                  <p className="cs-meta">The law firm referring this case (optional)</p>
                </div>
              </div>
              
              <div className="relative" ref={dropdownRef}>
                <div
                  className="flex items-center border-2 border-[var(--color-border)] rounded-xl px-4 py-3.5 bg-[var(--color-bg-elevated)] cursor-pointer hover:border-[var(--color-brand)] transition-colors"
                  onClick={() => setShowLawFirmDropdown(!showLawFirmDropdown)}
                >
                  <input
                    type="text"
                    value={formData.law_firm || lawFirmSearch}
                    onChange={(e) => {
                      setLawFirmSearch(e.target.value);
                      setFormData({ ...formData, law_firm: e.target.value });
                      setShowLawFirmDropdown(true);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLawFirmDropdown(true);
                    }}
                    placeholder="Select or type law firm name..."
                    className="flex-1 outline-none text-[var(--color-text-primary)] placeholder-[var(--color-text-dim)] bg-transparent"
                  />
                  <ChevronDown className={`w-5 h-5 text-[var(--color-text-dim)] transition-transform ${showLawFirmDropdown ? 'rotate-180' : ''}`} />
                </div>
                
                {showLawFirmDropdown && (
                  <div className="absolute z-50 w-full mt-2 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-xl shadow-lg max-h-60 overflow-auto">
                    {filteredLawFirms.length > 0 ? (
                      filteredLawFirms.map((firm, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setFormData({ ...formData, law_firm: firm });
                            setLawFirmSearch('');
                            setShowLawFirmDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)] transition-colors first:rounded-t-xl last:rounded-b-xl"
                        >
                          {firm}
                        </button>
                      ))
                    ) : null}

                    {lawFirmSearch && !lawFirms.includes(lawFirmSearch) && (
                      <button
                        onClick={handleAddNewLawFirm}
                        className="w-full px-4 py-3 text-left hover:bg-transparent text-[var(--color-verified)] border-t border-[var(--color-border-sub)] flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add "{lawFirmSearch}" as new law firm
                      </button>
                    )}

                    {filteredLawFirms.length === 0 && !lawFirmSearch && (
                      <div className="px-4 py-3 text-[var(--color-text-muted)] text-sm">
                        Start typing to search or add a new law firm
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════════
                SECTION 2: Client Business Details
            ═══════════════════════════════════════════════════════════════════ */}
            <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 border border-[var(--color-border)]">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-[var(--color-needs-human-tint)] flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[var(--color-brand)]" />
                </div>
                <div>
                  <h3 className="cs-h3">Client Business Details</h3>
                  <p className="cs-meta">Information about the business affected</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* Business Name */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--color-text-secondary)] mb-2">
                    Business Name <span className="text-[var(--color-brand)]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-dim)]">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <input
                      type="text"
                      value={formData.client_name}
                      onChange={(e) => { setFormData({ ...formData, client_name: e.target.value }); setValidationError(null); }}
                      placeholder="e.g., Pacific Surf Shop LLC"
                      className={`
                        w-full pl-12 pr-12 py-3
                        border-2 rounded-xl text-sm text-[var(--color-text-primary)]
                        transition-all duration-200
                        bg-[var(--color-bg-elevated)]
                        placeholder:text-[var(--color-text-dim)]
                        ${formData.client_name
                          ? 'border-[var(--color-verified)] focus:border-[var(--color-verified)] focus:ring-4 focus:ring-transparent'
                          : 'border-[var(--color-border)] focus:border-[var(--color-brand)] focus:ring-4 focus:ring-[var(--color-needs-human-tint)]'
                        }
                        focus:outline-none
                      `}
                    />
                    {formData.client_name && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--color-verified)]">
                        <Check className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Business Address */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--color-text-secondary)] mb-2">
                    Business Address <span className="text-[var(--color-brand)]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-dim)]">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <input
                      type="text"
                      value={formData.business_address}
                      onChange={(e) => { setFormData({ ...formData, business_address: e.target.value }); setValidationError(null); }}
                      placeholder="e.g., 456 Beach Blvd, San Diego, CA 92101"
                      className={`
                        w-full pl-12 pr-12 py-3
                        border-2 rounded-xl text-sm text-[var(--color-text-primary)]
                        transition-all duration-200
                        bg-[var(--color-bg-elevated)]
                        placeholder:text-[var(--color-text-dim)]
                        ${formData.business_address
                          ? 'border-[var(--color-verified)] focus:border-[var(--color-verified)] focus:ring-4 focus:ring-transparent'
                          : 'border-[var(--color-border)] focus:border-[var(--color-brand)] focus:ring-4 focus:ring-[var(--color-needs-human-tint)]'
                        }
                        focus:outline-none
                      `}
                    />
                    {formData.business_address && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--color-verified)]">
                        <Check className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-6">
            {/* ═══════════════════════════════════════════════════════════════════
                SECTION 3: Primary Contact
            ═══════════════════════════════════════════════════════════════════ */}
            <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 border border-[var(--color-border)]">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-[var(--color-needs-human-tint)] flex items-center justify-center">
                  <User className="w-5 h-5 text-[var(--color-brand)]" />
                </div>
                <div>
                  <h3 className="cs-h3">Primary Contact</h3>
                  <p className="cs-meta">Main point of contact for this claim</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* Contact Name */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--color-text-secondary)] mb-2">
                    Contact Name <span className="text-[var(--color-brand)]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-dim)]">
                      <User className="w-5 h-5" />
                    </div>
                    <input
                      type="text"
                      value={formData.claimant_name}
                      onChange={(e) => { setFormData({ ...formData, claimant_name: e.target.value }); setValidationError(null); }}
                      placeholder="e.g., Mike Wilson"
                      className={`
                        w-full pl-12 pr-12 py-3
                        border-2 rounded-xl text-sm text-[var(--color-text-primary)]
                        transition-all duration-200
                        bg-[var(--color-bg-elevated)]
                        placeholder:text-[var(--color-text-dim)]
                        ${formData.claimant_name
                          ? 'border-[var(--color-verified)] focus:border-[var(--color-verified)] focus:ring-4 focus:ring-transparent'
                          : 'border-[var(--color-border)] focus:border-[var(--color-brand)] focus:ring-4 focus:ring-[var(--color-needs-human-tint)]'
                        }
                        focus:outline-none
                      `}
                    />
                    {formData.claimant_name && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--color-verified)]">
                        <Check className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--color-text-secondary)] mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-dim)]">
                      <Mail className="w-5 h-5" />
                    </div>
                    <input
                      type="email"
                      value={formData.claimant_email}
                      onChange={(e) => setFormData({ ...formData, claimant_email: e.target.value })}
                      placeholder="e.g., mike@pacificsurfshop.com"
                      className="w-full pl-12 pr-4 py-3 border-2 border-[var(--color-border)] rounded-xl text-sm text-[var(--color-text-primary)] bg-[var(--color-bg-elevated)] focus:border-[var(--color-brand)] focus:ring-4 focus:ring-[var(--color-needs-human-tint)] focus:outline-none placeholder:text-[var(--color-text-dim)] transition-all duration-200"
                    />
                  </div>
                </div>
                
                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--color-text-secondary)] mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-dim)]">
                      <Phone className="w-5 h-5" />
                    </div>
                    <input
                      type="tel"
                      value={formData.claimant_phone}
                      onChange={(e) => setFormData({ ...formData, claimant_phone: e.target.value })}
                      placeholder="e.g., (555) 123-4567"
                      className="w-full pl-12 pr-4 py-3 border-2 border-[var(--color-border)] rounded-xl text-sm text-[var(--color-text-primary)] bg-[var(--color-bg-elevated)] focus:border-[var(--color-brand)] focus:ring-4 focus:ring-[var(--color-needs-human-tint)] focus:outline-none placeholder:text-[var(--color-text-dim)] transition-all duration-200"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </WizardV2Layout>
  );
}
